import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { contactService, getImageUrl } from '../services/contactService';
import { toast } from 'react-toastify';
import {
  FaPlus,
  FaSignOutAlt,
  FaMoon,
  FaSun,
  FaSearch,
  FaThLarge,
  FaList,
  FaStar,
  FaEdit,
  FaTrash,
  FaEnvelope,
  FaPhone,
  FaBirthdayCake,
  FaUsers,
} from 'react-icons/fa';
import ContactModal from '../components/ContactModal';

const Contacts = () => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [groupFilter, setGroupFilter] = useState('');
  const [orderBy, setOrderBy] = useState('name');
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, [searchQuery, groupFilter, orderBy]);

  const fetchContacts = async () => {
    try {
      const params = {
        search: searchQuery || undefined,
        group: groupFilter || undefined,
        order_by: orderBy,
      };
      const data = await contactService.getContacts(params);
      setContacts(data.contacts);
    } catch (error) {
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContact = () => {
    setEditingContact(null);
    setShowModal(true);
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    setShowModal(true);
  };

  const handleDeleteContact = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete ${name}?`)) {
      try {
        await contactService.deleteContact(id);
        toast.success('Contact deleted successfully');
        fetchContacts();
      } catch (error) {
        toast.error('Failed to delete contact');
      }
    }
  };

  const handleToggleFavorite = async (contact) => {
    try {
      await contactService.toggleFavorite(contact.id, !contact.is_favorite);
      fetchContacts();
      toast.success(
        contact.is_favorite ? 'Removed from favorites' : 'Added to favorites'
      );
    } catch (error) {
      toast.error('Failed to update favorite');
    }
  };

  const handleSaveContact = async (contactData) => {
    try {
      if (editingContact) {
        await contactService.updateContact(editingContact.id, contactData);
        toast.success('Contact updated successfully');
      } else {
        await contactService.createContact(contactData);
        toast.success('Contact created successfully');
      }
      setShowModal(false);
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save contact');
      throw error;
    }
  };

  const renderContactCard = (contact) => (
    <div key={contact.id} className="contact-card" id={`contact-card-${contact.id}`}>
      <div className="contact-card-header">
        <img
          src={getImageUrl(contact.photo_path)}
          alt={contact.name}
          className="contact-avatar"
          id={`contact-avatar-${contact.id}`}
        />
        <div className="contact-actions">
          <button
            id={`favorite-btn-${contact.id}`}
            className="btn-icon"
            onClick={() => handleToggleFavorite(contact)}
            title={contact.is_favorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <FaStar className={contact.is_favorite ? 'favorite-star' : ''} />
          </button>
          <button
            id={`edit-btn-${contact.id}`}
            className="btn-icon"
            onClick={() => handleEditContact(contact)}
            title="Edit contact"
          >
            <FaEdit />
          </button>
          <button
            id={`delete-btn-${contact.id}`}
            className="btn-icon"
            onClick={() => handleDeleteContact(contact.id, contact.name)}
            title="Delete contact"
          >
            <FaTrash />
          </button>
        </div>
      </div>
      <div className="contact-info">
        <h3 id={`contact-name-${contact.id}`}>{contact.name}</h3>
        {contact.email && (
          <p id={`contact-email-${contact.id}`}>
            <FaEnvelope /> {contact.email}
          </p>
        )}
        <p id={`contact-phone-${contact.id}`}>
          <FaPhone /> {contact.country_code} {contact.phone_number}
        </p>
        {contact.birthday && (
          <p id={`contact-birthday-${contact.id}`}>
            <FaBirthdayCake /> {new Date(contact.birthday).toLocaleDateString()}
          </p>
        )}
        {contact.group_category !== 'none' && (
          <span className="contact-badge" id={`contact-group-${contact.id}`}>
            <FaUsers /> {contact.custom_group || contact.group_category}
          </span>
        )}
      </div>
    </div>
  );

  const renderContactListItem = (contact) => (
    <div key={contact.id} className="contact-list-item" id={`contact-list-item-${contact.id}`}>
      <img
        src={getImageUrl(contact.photo_path)}
        alt={contact.name}
        className="contact-avatar"
        id={`contact-list-avatar-${contact.id}`}
      />
      <div className="contact-list-info">
        <div>
          <strong id={`contact-list-name-${contact.id}`}>{contact.name}</strong>
          {contact.is_favorite && <FaStar className="favorite-star" style={{ marginLeft: '8px' }} />}
        </div>
        <div id={`contact-list-email-${contact.id}`}>
          <FaEnvelope /> {contact.email || 'N/A'}
        </div>
        <div id={`contact-list-phone-${contact.id}`}>
          <FaPhone /> {contact.country_code} {contact.phone_number}
        </div>
        {contact.group_category !== 'none' && (
          <div id={`contact-list-group-${contact.id}`}>
            <FaUsers /> {contact.custom_group || contact.group_category}
          </div>
        )}
      </div>
      <div className="contact-list-actions">
        <button
          id={`list-favorite-btn-${contact.id}`}
          className="btn btn-outline"
          onClick={() => handleToggleFavorite(contact)}
        >
          <FaStar />
        </button>
        <button
          id={`list-edit-btn-${contact.id}`}
          className="btn btn-primary"
          onClick={() => handleEditContact(contact)}
        >
          <FaEdit />
        </button>
        <button
          id={`list-delete-btn-${contact.id}`}
          className="btn btn-danger"
          onClick={() => handleDeleteContact(contact.id, contact.name)}
        >
          <FaTrash />
        </button>
      </div>
    </div>
  );

  if (loading) {
    return <div className="loading" id="loading">Loading contacts...</div>;
  }

  return (
    <div id="contacts-page">
      <header className="header" id="contacts-header">
        <div className="header-content">
          <h1 id="header-title">Contact List</h1>
          <div className="header-actions">
            <span className="user-email" id="user-email">{user?.email}</span>
            <button
              id="theme-toggle-btn"
              className="btn-icon"
              onClick={toggleTheme}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <FaSun /> : <FaMoon />}
            </button>
            <button id="logout-btn" className="btn btn-danger" onClick={logout}>
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="container">
        <div className="toolbar" id="toolbar">
          <div className="toolbar-top">
            <div className="search-box">
              <input
                id="search-input"
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <FaSearch className="search-icon" />
            </div>
            <button id="create-contact-btn" className="btn btn-primary" onClick={handleCreateContact}>
              <FaPlus /> New Contact
            </button>
          </div>
          <div className="toolbar-bottom">
            <div className="filter-group">
              <label htmlFor="group-filter">Group:</label>
              <select
                id="group-filter"
                value={groupFilter}
                onChange={(e) => setGroupFilter(e.target.value)}
              >
                <option value="">All Groups</option>
                <option value="family">Family</option>
                <option value="work">Work</option>
                <option value="friends">Friends</option>
                <option value="other">Other</option>
                <option value="custom">Custom</option>
              </select>
            </div>
            <div className="filter-group">
              <label htmlFor="order-by">Sort by:</label>
              <select
                id="order-by"
                value={orderBy}
                onChange={(e) => setOrderBy(e.target.value)}
              >
                <option value="name">Name</option>
                <option value="is_favorite">Favorites</option>
              </select>
            </div>
            <div className="view-toggle">
              <button
                id="grid-view-btn"
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
                title="Grid view"
              >
                <FaThLarge />
              </button>
              <button
                id="list-view-btn"
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
                title="List view"
              >
                <FaList />
              </button>
            </div>
          </div>
        </div>

        {contacts.length === 0 ? (
          <div className="empty-state" id="empty-state">
            <div className="empty-state-icon">📇</div>
            <h2>No contacts yet</h2>
            <p>Click "New Contact" to add your first contact</p>
          </div>
        ) : (
          <div
            className={viewMode === 'grid' ? 'contacts-grid' : 'contacts-list'}
            id="contacts-container"
          >
            {viewMode === 'grid'
              ? contacts.map(renderContactCard)
              : contacts.map(renderContactListItem)}
          </div>
        )}
      </div>

      {showModal && (
        <ContactModal
          contact={editingContact}
          onClose={() => setShowModal(false)}
          onSave={handleSaveContact}
        />
      )}
    </div>
  );
};

export default Contacts;
